// Public spectator photo gallery. Renders the published photos in an
// orientation-aware masonry (each tile keeps its true aspect ratio, so portrait
// and landscape shots sit naturally side by side), with a per-photo caption.
// Tapping a photo opens the shared fullscreen lightbox, where visitors can swipe
// / arrow / tap between photos and download the current one.

import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import type { PublicPhoto } from '../../domain';
import { useTranslation } from '../../state/I18nContext';
import { PhotoLightbox } from '../PhotoLightbox';

interface PublicGalleryProps {
  photos: PublicPhoto[];
}

export function PublicGallery({ photos }: PublicGalleryProps) {
  const { t } = useTranslation();
  const [active, setActive] = useState<number | null>(null);

  if (photos.length === 0) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: 6, color: 'text.secondary' }}>
        <PhotoLibraryIcon sx={{ fontSize: 44 }} />
        <Typography variant="body2">{t('gallery.empty')}</Typography>
      </Stack>
    );
  }

  return (
    <>
      {/* CSS masonry: columns flow top-to-bottom; each tile avoids splitting and
          keeps its native aspect ratio so orientation is respected. */}
      <Box
        sx={{
          columnCount: { xs: 2, sm: 3 },
          columnGap: 1.25,
        }}
      >
        {photos.map((photo, i) => (
          <Box
            key={photo.id}
            onClick={() => setActive(i)}
            sx={{
              breakInside: 'avoid',
              mb: 1.25,
              borderRadius: 2,
              overflow: 'hidden',
              cursor: 'pointer',
              position: 'relative',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 10px 24px -16px rgba(0,0,0,0.8)',
              transition: 'transform .18s ease, box-shadow .18s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 16px 30px -16px rgba(244,63,94,0.5)',
              },
            }}
          >
            <Box
              component="img"
              src={photo.url}
              alt={photo.comment || t('gallery.alt')}
              loading="lazy"
              sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
                // Reserve the true aspect ratio up front so the masonry does not
                // reflow as images load.
                aspectRatio: `${photo.width} / ${photo.height}`,
                objectFit: 'cover',
              }}
            />
            {photo.comment && (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  px: 1,
                  py: 0.75,
                  color: '#fff',
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.72) 100%)',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {photo.comment}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <PhotoLightbox
        photos={photos}
        index={active}
        onIndexChange={setActive}
        onClose={() => setActive(null)}
        showDownload
        labels={{
          close: t('gallery.close'),
          download: t('gallery.download'),
          previous: t('gallery.previous'),
          next: t('gallery.next'),
          alt: t('gallery.alt'),
        }}
      />
    </>
  );
}
